import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import { LogOut, ArrowLeft, Calendar, User, Building, MapPin, DollarSign, FileText, Download, Mail, Phone, Users, Archive, X } from 'lucide-react';
import AddToVivierModal from './AddToVivierModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

const RHHSOfferDetailPage = () => {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [offer, setOffer] = useState(null);
  const [candidatures, setCandidatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showVivierModal, setShowVivierModal] = useState(false);
  const [selectedCandidature, setSelectedCandidature] = useState(null);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationPassword, setValidationPassword] = useState('');
  const [validating, setValidating] = useState(false);

  useEffect(() => {
    fetchOfferDetails();
    fetchCandidatures();
  }, [offerId]);

  const fetchOfferDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/job-offers/${offerId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const offerData = await response.json();
        setOffer(offerData);
      } else {
        toast.error('Erreur lors du chargement de l\'offre');
        navigate('/rh/horizon-solution');
      }
    } catch (error) {
      console.error('Error fetching offer details:', error);
      toast.error('Erreur de connexion');
      navigate('/rh/horizon-solution');
    } finally {
      setLoading(false);
    }
  };

  const fetchCandidatures = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/candidatures/job/${offerId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const candidaturesData = await response.json();
        // Sort candidatures: accepted ones first, then by creation date
        const sorted = candidaturesData.sort((a, b) => {
          if (a.status === 'accepte' && b.status !== 'accepte') return -1;
          if (b.status === 'accepte' && a.status !== 'accepte') return 1;
          return new Date(b.created_at) - new Date(a.created_at);
        });
        setCandidatures(sorted);
      }
    } catch (error) {
      console.error('Error fetching candidatures:', error);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Déconnexion réussie');
  };

  const handleBackToHS = () => {
    navigate('/rh/horizon-solution');
  };

  const handleDownloadFile = async (filename, type) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/download/${filename}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        toast.error('Erreur lors du téléchargement du fichier');
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Erreur de connexion lors du téléchargement');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non spécifiée';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusStyle = (candidature) => {
    let baseClass = 'candidature-status-compact';
    
    switch (candidature.status) {
      case 'accepte':
        baseClass += ' status-accepte';
        break;
      case 'refuse':
        baseClass += ' status-refuse';
        break;
      default:
        baseClass += ' status-nouveau';
    }
    
    return baseClass;
  };

  const getStatusText = (candidature) => {
    switch (candidature.status) {
      case 'accepte':
        return 'Accepté';
      case 'refuse':
        return 'Refusé';
      default:
        return 'Nouveau';
    }
  };

  const companyLogos = {
    'Horizon Solution': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/md0676vy_Design%20sans%20titre%20%282%29.png'
  };

  const handleAddToVivier = (candidature) => {
    setSelectedCandidature(candidature);
    setShowVivierModal(true);
  };

  const handleArchiveOffer = async () => {
    setValidating(false); // Reset validating state when opening modal
    setShowValidationModal(true);
  };

  const handleConfirmValidation = async () => {
    if (!validationPassword.trim()) {
      toast.error('Veuillez saisir votre mot de passe');
      return;
    }

    if (validating) {
      return; // Prevent multiple clicks
    }

    setValidating(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/job-offers/${offerId}/archive`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          password: validationPassword
        }),
      });

      if (response.ok) {
        toast.success('Offre archivée avec succès');
        setShowValidationModal(false);
        setValidationPassword('');
        navigate('/rh/horizon-solution');
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Erreur lors de l\'archivage');
      }
    } catch (error) {
      console.error('Error archiving offer:', error);
      toast.error('Erreur de connexion');
    } finally {
      setValidating(false);
    }
  };

  const handleConfirmAddToVivier = async (commentaire) => {
    if (!selectedCandidature) return;
    
    try {
      const token = localStorage.getItem('token');

      // First, check if candidate already exists in vivier
      const existingCheck = await fetch(`${API}/candidatures/vivier/search?query=${encodeURIComponent(selectedCandidature.prenom + ' ' + selectedCandidature.nom)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (existingCheck.ok) {
        const existingCandidates = await existingCheck.json();
        // Check if same name already exists (case insensitive)
        const isDuplicate = existingCandidates.some(existing => 
          existing.prenom.toLowerCase() === selectedCandidature.prenom.toLowerCase() &&
          existing.nom.toLowerCase() === selectedCandidature.nom.toLowerCase()
        );

        if (isDuplicate) {
          toast.error('la candidature est déjà dans le vivier des candidatures');
          return;
        }
      }

      // Use custom comment if provided, otherwise use default
      const finalCommentaire = commentaire || `Ajouté depuis l'offre HS: ${offer?.title}. Commentaire original: ${selectedCandidature.commentaire || 'Aucun'}`;

      // If no duplicate, proceed with adding to vivier
      const response = await fetch(`${API}/candidatures/vivier`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prenom: selectedCandidature.prenom,
          nom: selectedCandidature.nom,
          intitule_poste: offer?.title || 'N/A',
          societe: offer?.company || 'N/A',
          email: selectedCandidature.manager_email || selectedCandidature.email,
          telephone: selectedCandidature.manager_telephone || selectedCandidature.telephone,
          commentaire: finalCommentaire,
          cv_filename: selectedCandidature.cv_filename,
          lettre_filename: selectedCandidature.lettre_filename,
        }),
      });

      if (response.ok) {
        toast.success(`${selectedCandidature.prenom} ${selectedCandidature.nom} a été ajouté(e) au vivier des candidatures`);
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Erreur lors de l\'ajout au vivier');
      }
    } catch (error) {
      console.error('Error adding to vivier:', error);
      toast.error('Erreur de connexion');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!offer) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl text-destructive mb-4">Offre non trouvée</h1>
          <Button onClick={handleBackToHS}>Retour aux offres HS</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="rh-hs-offer-detail-page">
      {/* Header */}
      <header className="rh-header">
        <div className="flex items-center space-x-4">
          <Button
            onClick={handleBackToHS}
            variant="outline"
            size="sm"
            className="btn-outline"
            style={{ borderColor: '#eb5d2d', color: '#eb5d2d' }}
          >
            <ArrowLeft size={16} className="mr-2" />
            Retour
          </Button>
          <img
            src={companyLogos['Horizon Solution']}
            alt="Logo Horizon Solution"
            className="rh-header-logo"
          />
        </div>
        <h1>Détail offre RH - Horizon Solution</h1>
        <Button
          onClick={handleLogout}
          variant="outline"
          size="sm"
          className="btn-outline"
          style={{ borderColor: '#eb5d2d', color: '#eb5d2d' }}
        >
          <LogOut size={16} className="mr-2" />
          Déconnexion
        </Button>
      </header>

      {/* Main Content */}
      <main className="rh-main">
        {/* Offer Header */}
        <div className="offer-detail-header-compact">
          <h2>{offer.company} : {offer.title}</h2>
          <div className="offer-detail-info-line">
            <div className="offer-detail-info-item-compact">
              <User className="offer-detail-info-icon-compact" />
              <span>{offer.creator_name || 'Inconnu'}</span>
            </div>
            <div className="offer-detail-info-separator">•</div>
            <div className="offer-detail-info-item-compact">
              <Calendar className="offer-detail-info-icon-compact" />
              <span>{formatDate(offer.date_arrivee)}</span>
            </div>
            <div className="offer-detail-info-separator">•</div>
            <div className="offer-detail-info-item-compact">
              <MapPin className="offer-detail-info-icon-compact" />
              <span>{offer.lieu_travail || 'Non spécifié'}</span>
            </div>
            {offer.type_poste && (
              <>
                <div className="offer-detail-info-separator">•</div>
                <div className="offer-detail-info-item-compact">
                  <span>{offer.type_poste}</span>
                </div>
              </>
            )}
            {offer.taux_horaire && (
              <>
                <div className="offer-detail-info-separator">•</div>
                <div className="offer-detail-info-item-compact">
                  <DollarSign className="offer-detail-info-icon-compact" />
                  <span>{offer.taux_horaire}€/h</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Offer Description */}
        <Card className="offer-description-card-compact">
          <CardContent className="p-4">
            <h3 className="text-lg font-semibold mb-3">Description détaillée</h3>
            <div className="offer-description-content-compact">
              <div className="mb-3">
                <h4 className="font-medium mb-1 text-sm text-primary">Missions</h4>
                <p className="text-gray-700 text-sm whitespace-pre-wrap">{offer.description}</p>
              </div>
              {offer.qualites_attendues && (
                <div>
                  <h4 className="font-medium mb-1 text-sm text-primary">Qualités attendues</h4>
                  <p className="text-gray-700 text-sm whitespace-pre-wrap">{offer.qualites_attendues}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Candidatures Table - NO "Add Candidature" button for RH HS */}
        <Card className="candidatures-card-compact">
          <CardContent className="p-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-semibold">
                Candidatures ({candidatures.length})
              </h3>
              {user?.role === 'rh' && offer?.status === 'en_cours' && (
                <Button
                  onClick={handleArchiveOffer}
                  variant="outline"
                  className="text-sm px-3 py-2"
                  style={{ borderColor: '#dc2626', color: '#dc2626' }}
                >
                  <Archive size={16} className="mr-2" />
                  Archiver
                </Button>
              )}
            </div>
            <div className="candidatures-table-container-compact">
              <table className="candidatures-table-compact">
                <thead>
                  <tr>
                    <th>Candidat</th>
                    <th>Documents de base</th>
                    <th>Documents HS</th>
                    <th>Contact</th>
                    <th>Commentaire</th>
                    <th>Statut</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {candidatures.map((candidature) => (
                    <tr 
                      key={candidature.id}
                      className={candidature.status === 'accepte' ? 'candidature-accepted-priority' : ''}
                    >
                      <td>
                        <div className="candidature-name-compact">
                          {candidature.prenom} {candidature.nom}
                          {candidature.status === 'accepte' && (
                            <span className="priority-badge">ACCEPTÉ</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="candidature-files-compact">
                          {candidature.cv_filename ? (
                            <Button
                              variant="outline"
                              size="sm"
                              className="mr-1 text-xs px-2 py-1"
                              onClick={() => handleDownloadFile(candidature.cv_filename, 'CV')}
                            >
                              <Download size={12} className="mr-1" />
                              CV
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              className="mr-1 text-xs px-2 py-1"
                              disabled
                            >
                              CV indisponible
                            </Button>
                          )}
                          {candidature.lettre_filename ? (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs px-2 py-1"
                              onClick={() => handleDownloadFile(candidature.lettre_filename, 'Lettre')}
                            >
                              <FileText size={12} className="mr-1" />
                              Lettre
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs px-2 py-1"
                              disabled
                            >
                              Lettre indisponible
                            </Button>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="hs-documents-compact">
                          {candidature.piece_identite_filename && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="hs-doc-btn"
                              onClick={() => handleDownloadFile(candidature.piece_identite_filename, 'Pièce identité')}
                            >
                              <Download size={10} className="mr-1" />
                              ID
                            </Button>
                          )}
                          {candidature.attestation_secu_filename && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="hs-doc-btn"
                              onClick={() => handleDownloadFile(candidature.attestation_secu_filename, 'Attestation sécu')}
                            >
                              <Download size={10} className="mr-1" />
                              SÉCU
                            </Button>
                          )}
                          {candidature.justificatif_domicile_filename && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="hs-doc-btn"
                              onClick={() => handleDownloadFile(candidature.justificatif_domicile_filename, 'Justificatif domicile')}
                            >
                              <Download size={10} className="mr-1" />
                              DOM
                            </Button>
                          )}
                          {candidature.rib_filename && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="hs-doc-btn"
                              onClick={() => handleDownloadFile(candidature.rib_filename, 'RIB')}
                            >
                              <Download size={10} className="mr-1" />
                              RIB
                            </Button>
                          )}
                          {!candidature.piece_identite_filename && 
                           !candidature.attestation_secu_filename && 
                           !candidature.justificatif_domicile_filename && 
                           !candidature.rib_filename && (
                            <span className="text-gray-400 text-xs">Aucun document HS</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="candidature-contact-compact">
                          {(candidature.manager_email || candidature.email) && (
                            <div className="contact-item-compact">
                              <Mail size={12} />
                              <span>{candidature.manager_email || candidature.email}</span>
                            </div>
                          )}
                          {(candidature.manager_telephone || candidature.telephone) && (
                            <div className="contact-item-compact">
                              <Phone size={12} />
                              <span>{candidature.manager_telephone || candidature.telephone}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="candidature-comment-compact">
                          {candidature.commentaire || '-'}
                          {candidature.raison_refus && (
                            <div className="refus-reason">
                              <strong>Refus:</strong> {candidature.raison_refus}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className={getStatusStyle(candidature)}>
                          {getStatusText(candidature)}
                        </div>
                      </td>
                      <td>
                        <div className="candidature-actions-compact">
                          <Button
                            variant="outline"
                            size="sm"
                            className="btn-add-to-vivier"
                            onClick={() => handleAddToVivier(candidature)}
                            title="Ajouter cette candidature au vivier"
                          >
                            <Users size={12} className="mr-1" />
                            Ajouter au vivier
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {candidatures.length === 0 && (
              <div className="empty-state-compact">
                <p>Aucune candidature pour cette offre</p>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Add To Vivier Modal */}
      {showVivierModal && selectedCandidature && (
        <AddToVivierModal
          isOpen={showVivierModal}
          onClose={() => setShowVivierModal(false)}
          onConfirm={handleConfirmAddToVivier}
          candidatureName={`${selectedCandidature.prenom} ${selectedCandidature.nom}`}
        />
      )}

      {/* Validation Modal */}
      {showValidationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold mb-4">Archiver l'offre</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowValidationModal(false);
                  setValidationPassword('');
                  setValidating(false);
                }}
                className="p-1"
              >
                <X size={20} />
              </Button>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-3">
                Pour archiver cette offre, veuillez saisir votre mot de passe RH :
              </p>
              <input
                type="password"
                value={validationPassword}
                onChange={(e) => setValidationPassword(e.target.value)}
                placeholder="Mot de passe"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                disabled={validating}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !validating) {
                    handleConfirmValidation();
                  }
                }}
              />
            </div>
            
            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowValidationModal(false);
                  setValidationPassword('');
                  setValidating(false);
                }}
                disabled={validating}
              >
                Annuler
              </Button>
              <Button
                onClick={handleConfirmValidation}
                disabled={validating || !validationPassword.trim()}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {validating ? 'Archivage en cours...' : 'Archiver'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RHHSOfferDetailPage;